import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from 'react-router';
import ROUTES from 'library/constants/routes';
import { setBrowseItemsFilters, getBrowseItems as getBrowseItemsAction } from 'store/browse-items/actions';
import {
  getWishlistItems as getWishlistItemsAction,
  setWishlistItemsFilters,
} from 'store/wishlist-items/actions';
import {
  getBrowseItemsFilters as getBrowseItemsFiltersSelector,
  getSuggestedBrowseItemsOptions as getSuggestedBrowseItemsOptionsSelector,
} from 'store/browse-items/selectors';
import { getSuggestedItemsLoadingState } from 'store/suggested-items/selectors';
import {
  getWishlistItemsFilters,
  getWishlistSuggestedItemsOptions,
} from 'store/wishlist-items/selectors';
import { resetSuggestedItems as resetSuggestedItemsAction } from 'store/suggested-items/actions';
import {
  getSuggestedProjectItemsOptions as getSuggestedProjectItemsOptionsSelector,
} from 'store/project-items/selectors';

import { getViewMode } from 'store/shared/selectors';
import { FILTER_KEYS } from 'store/browse-items/fixtures';
import { getUser } from 'store/users/actions';
import ActivityWatcher from 'library/components/activity-watcher';
import Browse from './browse';
import Wishlist from './wishlist';
import Projects from './projects';
import Settings from './settings';
import { Container } from './styles';
import { setProjectItemsFilters, getProjectItems as getProjectItemsAction } from '../../store/project-items/actions';
import { logout } from '../../library/auth';
import { getUser as getUserSelector } from '../../store/users/selectors';

const Dashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const suggestedBrowseItemsOptions = useSelector(getSuggestedBrowseItemsOptionsSelector);
  const suggestedProjectItemsOptions = useSelector(getSuggestedProjectItemsOptionsSelector);
  const suggestedWishlistItemsOptions = useSelector(getWishlistSuggestedItemsOptions);
  const isSuggestionLoading = useSelector(getSuggestedItemsLoadingState);
  const mode = useSelector(getViewMode);
  const user = useSelector(getUserSelector);
  const filters = useSelector(getBrowseItemsFiltersSelector);
  const wishlistFilters = useSelector(getWishlistItemsFilters);

  useEffect(() => {
    // todo - move to settings page if 'user' data won't contain role related attrs
    dispatch(getUser());
  }, []);

  const location = useLocation();

  const handleBrowseItemsQueryChange = ({ type, ...option }) => {
    dispatch(setBrowseItemsFilters({
      ...filters,
      ...(type === 'tag' ? { [FILTER_KEYS.TAGS]: [...filters[FILTER_KEYS.TAGS], { ...option, field: FILTER_KEYS.TAGS }] } : {}),
      ...(type === 'item' ? { [FILTER_KEYS.ITEMS]: [...filters[FILTER_KEYS.ITEMS], { ...option, field: FILTER_KEYS.ITEMS }] } : {}),
      ...(type === 'color' ? { [FILTER_KEYS.COLORS]: [...filters[FILTER_KEYS.COLORS], ...option.values.map((value) => ({ ...value, field: FILTER_KEYS.COLORS }))] } : {}),
      ...(type === 'query' ? { [FILTER_KEYS.QUERY]: filters[FILTER_KEYS.QUERY] !== '' ? Array.from(new Set(filters[FILTER_KEYS.QUERY].split(',').concat([option.name]))).join(',') : option.name } : {}),
    }));

    dispatch(getBrowseItemsAction());
  };

  const handleProjectItemsQueryChange = ({ type, ...option }) => {
    dispatch(setProjectItemsFilters({
      ...filters,
      ...(type === 'tag' ? { [FILTER_KEYS.TAGS]: [...filters[FILTER_KEYS.TAGS], { ...option, field: FILTER_KEYS.TAGS }] } : {}),
      ...(type === 'item' ? { [FILTER_KEYS.ITEMS]: [...filters[FILTER_KEYS.ITEMS], { ...option, field: FILTER_KEYS.ITEMS }] } : {}),
    }));

    dispatch(getProjectItemsAction());
  };

  const handleWishlistItemsQueryChange = ({ type, ...option }) => {
    dispatch(setWishlistItemsFilters({
      ...wishlistFilters,
      ...(type === 'tag' ? { [FILTER_KEYS.TAGS]: [...filters[FILTER_KEYS.TAGS], { ...option, field: FILTER_KEYS.TAGS }] } : {}),
      ...(type === 'item' ? { [FILTER_KEYS.ITEMS]: [...filters[FILTER_KEYS.ITEMS], { ...option, field: FILTER_KEYS.ITEMS }] } : {}),
    }));

    dispatch(getWishlistItemsAction());
  };

  const handleSearchQueryReset = () => {
    dispatch(resetSuggestedItemsAction());
  };

  const handleLogout = () => logout(history);

  return (

    <Container>
      <Switch location={location}>
        <Route
          path={ROUTES.BROWSE}
        >
          <Browse
            searchTypes={['tag', 'item', 'color']}
            onSuggestionSelect={handleBrowseItemsQueryChange}
            onSuggestionsReset={handleSearchQueryReset}
            isSuggestionLoading={isSuggestionLoading}
            suggestions={suggestedBrowseItemsOptions}
            searchPlaceholder="Search for items, colors or tags..."
            queryParameter={FILTER_KEYS.QUERY}
          />
        </Route>
        <Route
          path={ROUTES.PROJECTS}
        >
          <Projects
            searchTypes={[]}
            onSuggestionSelect={handleProjectItemsQueryChange}
            onSuggestionsReset={handleSearchQueryReset}
            isSuggestionLoading={isSuggestionLoading}
            suggestions={suggestedProjectItemsOptions || []}
            searchPlaceholder="Search for projects..."
          />
        </Route>
        <Route
          path={ROUTES.WISHLIST}
        >
          <Wishlist
            searchTypes={['set', 'item', 'color']}
            onSuggestionSelect={handleWishlistItemsQueryChange}
            onSuggestionsReset={handleSearchQueryReset}
            isSuggestionLoading={isSuggestionLoading}
            suggestions={suggestedWishlistItemsOptions}
            searchPlaceholder="Search for items, colors or tags..."
          />
        </Route>
        <Route
          exact
          path={ROUTES.SETTINGS}
          component={Settings}
        />
        <Route path="*">
          <Redirect to={`${ROUTES.BROWSE}${mode}`} />
        </Route>
      </Switch>
      <ActivityWatcher
        enabled={Boolean(user?.id)}
        timeout={user?.inactivityTimer}
        onInactive={handleLogout}
      />
    </Container>
  );
};

export default Dashboard;
